import {
  type CipherObject,
  canonical,
  createDeviceKeys,
  type ObjectScope,
  unwrapDataKey,
  wrapDataKey,
} from "./cryptoV2";

export type DeviceIdentity = { accountId: string; deviceId: string };
type DeviceKeys = Awaited<ReturnType<typeof createDeviceKeys>>;
export type UnlockedDevice = DeviceIdentity &
  DeviceKeys & { scope: ObjectScope; wrapped: CipherObject; dataKey: CryptoKey; remembered: boolean };
type RememberedDevice = DeviceIdentity & DeviceKeys & { scope: ObjectScope; wrapped: CipherObject };

// One IndexedDB entry is one explicitly trusted account/device/dataset. There
// is no recovery secret, recovery private key, password or Google token here.
function entryKey(accountId: string, scope: ObjectScope) {
  return canonical([accountId, scope.datasetId, scope.generation, scope.keyVersion]);
}
export class DeviceVault {
  private session = new Map<string, UnlockedDevice>();
  constructor(private readonly databaseName = "classroom-sync-v2-device-keys") {}

  private async database() {
    if (!globalThis.indexedDB) throw new Error("sync-device-storage-unavailable");
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.databaseName, 1);
      request.onupgradeneeded = () => request.result.createObjectStore("devices");
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error("sync-device-storage-unavailable"));
      request.onblocked = () => reject(new Error("sync-device-storage-blocked"));
    });
  }
  private async write(key: string, value?: RememberedDevice, setupAccount?: string) {
    const db = await this.database();
    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction("devices", "readwrite");
        const store = transaction.objectStore("devices");
        let failure = "sync-device-storage-failed";
        const put = () => {
          if (value) store.put(value, key);
          else store.delete(key);
          if (setupAccount && value) store.put(value, `setup:${setupAccount}`);
        };
        if (setupAccount && value) {
          // Two tabs can both observe no bootstrap before either writes it.
          // Check and save under one IDB transaction so the second tab cannot
          // orphan a root enrolled with the first tab's device secret.
          const previous = store.get(`setup:${setupAccount}`);
          previous.onsuccess = () => {
            if (previous.result) {
              failure = "sync-setup-in-progress";
              transaction.abort();
            } else put();
          };
        } else put();
        transaction.oncomplete = () => resolve();
        transaction.onabort = transaction.onerror = () => reject(new Error(failure));
      });
    } finally {
      db.close();
    }
  }
  private async forgetPersistedAccount(accountId: string) {
    const db = await this.database();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction("devices", "readwrite");
        const request = tx.objectStore("devices").openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          if ((cursor.value as RememberedDevice).accountId === accountId) cursor.delete();
          cursor.continue();
        };
        tx.oncomplete = () => resolve();
        tx.onabort = tx.onerror = () => reject(new Error("sync-device-storage-failed"));
      });
    } finally {
      db.close();
    }
  }
  async create(
    identity: DeviceIdentity,
    scope: ObjectScope,
    material: string,
    remember: boolean,
    keys?: DeviceKeys,
    pendingSetup = false,
  ): Promise<UnlockedDevice> {
    const deviceKeys = keys ?? (await createDeviceKeys());
    const wrapped = await wrapDataKey(deviceKeys.wrapping, scope, material);
    const saved = {
      accountId: identity.accountId,
      deviceId: identity.deviceId,
      wrapping: deviceKeys.wrapping,
      signing: deviceKeys.signing,
      agreement: deviceKeys.agreement,
      scope,
      wrapped,
    };
    const device = { ...saved, dataKey: await unwrapDataKey(saved.wrapping, scope, wrapped), remembered: remember };
    const id = entryKey(identity.accountId, scope);
    // Failure to persist must be visible; never show a successful "remembered"
    // choice when the browser rejected the CryptoKey structured clone.
    if (remember) await this.write(id, saved, pendingSetup ? identity.accountId : undefined);
    else await this.forgetPersistedAccount(identity.accountId);
    this.session.set(id, device);
    return device;
  }
  prepareSetup(identity: DeviceIdentity, scope: ObjectScope, material: string, keys: DeviceKeys) {
    // The encrypted key and its setup pointer commit together BEFORE enrolling
    // remotely. An interrupted first enrollment must not orphan a cloud root.
    return this.create(identity, scope, material, true, keys, true);
  }
  async pendingSetup(accountId: string): Promise<UnlockedDevice | null> {
    const db = await this.database();
    let saved: RememberedDevice | undefined;
    try {
      saved = await new Promise((resolve, reject) => {
        const tx = db.transaction("devices", "readonly");
        const request = tx.objectStore("devices").get(`setup:${accountId}`);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(new Error("sync-device-storage-failed"));
      });
    } finally {
      db.close();
    }
    if (!saved) return null;
    if (saved.accountId !== accountId) throw new Error("sync-device-scope-mismatch");
    const device = await this.unlock(accountId, saved.scope);
    if (!device || device.deviceId !== saved.deviceId || canonical(device.wrapped) !== canonical(saved.wrapped))
      throw new Error("sync-device-scope-mismatch");
    return device;
  }
  finishSetup(accountId: string) {
    return this.write(`setup:${accountId}`);
  }
  async unlock(accountId: string, scope: ObjectScope): Promise<UnlockedDevice | null> {
    const id = entryKey(accountId, scope);
    const session = this.session.get(id);
    if (session) return session;
    const db = await this.database();
    let saved: RememberedDevice | undefined;
    try {
      saved = await new Promise((resolve, reject) => {
        const transaction = db.transaction("devices", "readonly");
        const request = transaction.objectStore("devices").get(id);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(new Error("sync-device-storage-failed"));
      });
    } finally {
      db.close();
    }
    if (!saved) return null;
    if (
      saved.accountId !== accountId ||
      canonical(saved.scope) !== canonical(scope) ||
      saved.wrapping.extractable ||
      saved.signing.privateKey.extractable ||
      saved.agreement.privateKey.extractable
    )
      throw new Error("sync-device-scope-mismatch");
    const device = { ...saved, dataKey: await unwrapDataKey(saved.wrapping, scope, saved.wrapped), remembered: true };
    this.session.set(id, device);
    return device;
  }
  async forget(accountId: string, scope: ObjectScope) {
    const id = entryKey(accountId, scope);
    this.session.delete(id);
    await this.write(id);
  }
  lock() {
    this.session.clear();
  }
}
